import {
  AgeType,
  Guest,
} from "../api/contracts/reservation";
import {
  BedAgeSpanSettings,
  GetWBEConfigResponse,
  GuestTypesConfig,
  WBEMaxConfigGuests,
} from "../api/contracts/site-config";
import { getBedTypeForAge } from "./bedTypeUtils";

const AGE_TYPE_TO_CONFIG_KEY: Record<AgeType, keyof WBEMaxConfigGuests> = {
  adult: "adults",
  teenager: "teenagers",
  child: "children",
  infant: "infants",
};

const buildDefaultGuest = (
  ageType: AgeType,
  bedAgeSpanSettings: BedAgeSpanSettings | undefined,
  guestTypesConfig: GuestTypesConfig | undefined,
): Guest => {
  if (ageType === "adult" || ageType === "teenager") {
    return { ageType, bedCapacityType: "full" };
  }

  const minAge =
    ageType === "child"
      ? guestTypesConfig?.child.minAge
      : guestTypesConfig?.infant.minAge;
  const bedCapacityType =
    minAge != null && bedAgeSpanSettings
      ? getBedTypeForAge(minAge, bedAgeSpanSettings)
      : ageType === "infant"
        ? "cot"
        : "full";

  return {
    ageType,
    bedCapacityType,
    ...(minAge != null && { age: minAge }),
  };
};

export const resizeGuestList = (
  unitGuests: Guest[],
  ageType: AgeType,
  targetCount: number,
  wbeConfig: Pick<
    GetWBEConfigResponse,
    "maxConfig" | "bedAgeSpanSettings" | "guestTypesConfig"
  >,
): Guest[] => {
  const maxForType =
    wbeConfig.maxConfig.maxGuests[AGE_TYPE_TO_CONFIG_KEY[ageType]] ?? 0;
  const otherGuests = unitGuests.filter((g) => g.ageType !== ageType);
  const existingOfType = unitGuests.filter((g) => g.ageType === ageType);

  const limitedByType = Math.min(targetCount, maxForType);
  const remainingTotal = wbeConfig.maxConfig.maxTotalGuests - otherGuests.length;
  const adjustedCount = Math.max(0, Math.min(limitedByType, remainingTotal));

  const sameType: Guest[] = [];
  for (let i = 0; i < adjustedCount; i++) {
    sameType.push(
      existingOfType[i] ??
        buildDefaultGuest(
          ageType,
          wbeConfig.bedAgeSpanSettings,
          wbeConfig.guestTypesConfig,
        ),
    );
  }

  return [...otherGuests, ...sameType];
};
